<template>
  <v-card
    style="text-align: center"
    :flat="flat == undefined || flat == false ? false : true"
    :loading="!sourceLoaded"
  >
    <v-card-text>
      <v-btn
        outlined
        icon
        class="ma-2"
        :color="color"
        @click.native="playing ? pause() : play()"
        :disabled="!loaded || !sourceLoaded"
        :small="small"
      >
        <v-icon v-if="!playing || paused">{{ playIcon }}</v-icon>
        <v-icon v-else>{{ pauseIcon }}</v-icon>
      </v-btn>
      <v-btn
        outlined
        icon
        class="ma-2"
        :color="color"
        @click.native="stop()"
        :disabled="!loaded || !sourceLoaded"
        :small="small"
      >
        <v-icon>{{ stopIcon }}</v-icon>
      </v-btn>
      <v-btn
        outlined
        icon
        class="ma-2"
        :color="color"
        @click.native="mute()"
        :disabled="!loaded"
        :small="small"
      >
        <v-icon v-if="!isMuted">{{ volumeHighIcon }}</v-icon>
        <v-icon v-else>{{ volumeMuteIcon }}</v-icon>
      </v-btn>
      <v-btn
        outlined
        icon
        class="ma-2"
        :color="color"
        @click.native="loaded ? download() : reload()"
        v-if="!loaded"
      >
        <v-icon>{{ refreshIcon }}</v-icon>
      </v-btn>
      <v-btn
        outlined
        icon
        class="ma-2"
        :color="color"
        @click.native="loaded ? download() : reload()"
        v-if="loaded && downloadable"
        :small="small"
      >
        <v-icon>{{ downloadIcon }}</v-icon>
      </v-btn>
      <v-slider
        v-model="playerVolume"
        :prepend-icon="volumeHighIcon"
        max="1"
        step="0.01"
        min="0"
      />
      <v-progress-linear
        v-model="percentage"
        height="5"
        style="margin-top: 15px; margin-bottom: 15px"
        @click.native="setPosition()"
        :disabled="!loaded"
      />
      <p>{{ currentTime }} / {{ duration }}</p>
    </v-card-text>
    <audio
      v-if="mimeType == 'audio/mp3'"
      id="player"
      ref="player"
      v-on:ended="ended"
      v-on:canplay="canPlay"
      v-auth-audio-mp3="file"
      @sourceLoaded="handleSourceLoaded"
      crossorigin
    ></audio>
    <audio
      v-else-if="mimeType == 'audio/wav'"
      id="player"
      ref="player"
      v-on:ended="ended"
      v-on:canplay="canPlay"
      v-auth-audio-wav="file"
      @sourceLoaded="handleSourceLoaded"
    ></audio>
  </v-card>
</template>

<script>
import formatDateTime from "@/utils/mixins/formatDateTime";
import formatDuration from "@/utils/mixins/formatDuration";
import downloadFile from "@/utils/mixins/downloadFile";
import showErrors from "@/utils/mixins/showErrors";
import isMobile from "@/utils/mixins/isMobile";

export default {
  name: "AudioPlayer",
  mixins: [showErrors, formatDateTime, formatDuration, downloadFile, isMobile],
  props: {
    flat: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    file: {
      type: String,
      default: null,
    },
    autoPlay: {
      type: Boolean,
      default: false,
    },
    ended: {
      type: Function,
      default: () => {},
    },
    canPlay: {
      type: Function,
      default: () => {},
    },
    color: {
      type: String,
      default: null,
    },
    downloadable: {
      type: Boolean,
      default: false,
    },
    playIcon: {
      type: String,
      default: "mdi-play",
    },
    pauseIcon: {
      type: String,
      default: "mdi-pause",
    },
    stopIcon: {
      type: String,
      default: "mdi-stop",
    },
    refreshIcon: {
      type: String,
      default: "mdi-refresh",
    },
    downloadIcon: {
      type: String,
      default: "mdi-download",
    },
    volumeHighIcon: {
      type: String,
      default: "mdi-volume-high",
    },
    volumeMuteIcon: {
      type: String,
      default: "mdi-volume-mute",
    },
    mimeType: {
      type: String,
      require: true,
      validator: (value) => ["audio/mp3", "audio/wav"].includes(value),
    },
  },
  data: () => ({
    firstPlay: true,
    isMuted: false,
    loaded: false,
    playing: false,
    paused: false,
    percentage: 0,
    currentTime: "00:00:00",
    audio: undefined,
    totalDuration: 0,
    playerVolume: 0.5,
    downloading: false,
    sourceLoaded: false,
  }),
  computed: {
    duration() {
      return this.audio ? this.formatDuration(this.totalDuration) : "";
    },
    downloadFileName() {
      const url = new URL(this.file);
      var path = String(url.pathname);
      return (
        path.substring(path.lastIndexOf("/") + 1) +
        "." +
        (this.mimeType == "audio/mp3" ? "mp3" : "wav")
      );
    },
  },
  watch: {
    file() {
      this.sourceLoaded = false;
      this.stop();
    },
  },
  methods: {
    handleSourceLoaded(event) {
      // console.log(">>> handleSourceLoaded: ", event, event.detail.successfully);
      this.sourceLoaded = true;
    },
    setPosition() {
      if (this.audio)
        this.audio.currentTime = parseInt(
          (this.audio.duration / 100) * this.percentage
        );
    },
    stop() {
      if (this.audio) {
        this.audio.pause();
        this.paused = true;
        this.playing = false;
        this.audio.currentTime = 0;
      }
    },
    play() {
      if (this.audio) {
        if (this.playing) return;
        this.audio.play().then((_) => (this.playing = true));
        this.paused = false;
      }
    },
    pause() {
      if (this.audio) {
        this.paused = !this.paused;
        this.paused ? this.audio.pause() : this.audio.play();
      }
    },
    download() {
      if (this.audio) {
        var that = this;
        this.audio.pause();
        this.downloading = true;
        this.downloadFile(
          this.file,
          "get",
          {},
          this.downloadFileName,
          this.mimeType,
          function () {
            that.downloading = false;
          },
          null,
          true
        );
      }
    },
    mute() {
      if (this.audio) {
        this.isMuted = !this.isMuted;
        this.audio.muted = this.isMuted;
      }
    },
    reload() {
      if (this.audio) this.audio.load();
    },
    _handleLoaded() {
      if (this.audio && this.audio.readyState >= 2) {
        if (this.audio.duration === Infinity) {
          // Fix duration for streamed audio source or blob based
          // https://stackoverflow.com/questions/38443084/how-can-i-add-predefined-length-to-audio-recorded-from-mediarecorder-in-chrome/39971175#39971175
          this.audio.currentTime = 1e101;
          this.audio.ontimeupdate = () => {
            this.audio.ontimeupdate = () => {};
            this.audio.currentTime = 0;
            this.totalDuration = parseInt(this.audio.duration);
            this.loaded = true;
          };
        } else {
          this.totalDuration = parseInt(this.audio.duration);
          this.loaded = true;
        }

        if (this.autoPlay) this.audio.play();
      } else {
        throw new Error("Failed to load sound file");
      }
    },
    _handlePlayingUI(event) {
      if (this.audio) {
        this.audio.volume = this.playerVolume;
        this.percentage = (this.audio.currentTime / this.audio.duration) * 100;
        this.currentTime = this.formatDuration(this.audio.currentTime);
        this.playing = true;
      }
    },
    _handlePlayPause(event) {
      if (this.audio) {
        if (event.type === "play" && this.firstPlay) {
          // in some situations, audio.currentTime is the end one on chrome
          this.audio.currentTime = 0;
          if (this.firstPlay) {
            this.firstPlay = false;
          }
        }
        if (
          event.type === "pause" &&
          this.paused === false &&
          this.playing === false
        ) {
          this.currentTime = "00:00:00";
        }
      }
    },
    _handleEnded() {
      this.paused = this.playing = false;
    },
    init() {
      if (this.audio) {
        this.audio.addEventListener("timeupdate", this._handlePlayingUI);
        this.audio.addEventListener("loadeddata", this._handleLoaded);
        this.audio.addEventListener("pause", this._handlePlayPause);
        this.audio.addEventListener("play", this._handlePlayPause);
        this.audio.addEventListener("ended", this._handleEnded);
      }
    },
  },
  mounted() {
    this.audio = this.$refs.player;
    this.init();
  },
  beforeDestroy() {
    if (this.audio) {
      this.audio.removeEventListener("timeupdate", this._handlePlayingUI);
      this.audio.removeEventListener("loadeddata", this._handleLoaded);
      this.audio.removeEventListener("pause", this._handlePlayPause);
      this.audio.removeEventListener("play", this._handlePlayPause);
      this.audio.removeEventListener("ended", this._handleEnded);
    }
  },
};
</script>

<i18n>
{
  "en": {
  },
  "de": {
  }
}
</i18n>
